/**
 * Thumbnail Upload Component with LinkedIn-style cropping
 * Allows users to upload an image and adjust the visible area
 */

import { useState, useRef } from "react"
import { Upload, X, Loader2, Check, Move } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { cn, resolveMediaUrl } from "@/lib/utils"
import { uploadFile } from "@/services/upload.service"

interface ThumbnailUploadProps {
  label?: string
  currentUrl?: string
  onUpload: (url: string) => void
  onRemove?: () => void
  aspectRatio?: number
  className?: string
}

export function ThumbnailUpload({
  label = "Thumbnail",
  currentUrl,
  onUpload,
  onRemove,
  aspectRatio = 16 / 9,
  className,
}: ThumbnailUploadProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith("image/")) {
      setError("Please select an image file")
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("Image must be less than 10MB")
      return
    }

    setError(null)
    setSelectedFile(file)

    const reader = new FileReader()
    reader.onload = (event) => {
      setImageUrl(event.target?.result as string)
      setIsDialogOpen(true)
    }
    reader.readAsDataURL(file)

    if (inputRef.current) inputRef.current.value = ""
  }

  const getCroppedImage = async (): Promise<Blob | null> => {
    const image = imgRef.current
    if (!image) return null

    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")
    if (!ctx) return null

    const naturalWidth = image.naturalWidth
    const naturalHeight = image.naturalHeight

    let cropWidth = naturalWidth
    let cropHeight = naturalHeight
    let cropX = 0
    let cropY = 0

    // Perform a centered crop to match requested aspect ratio.
    if (naturalWidth / naturalHeight > aspectRatio) {
      cropWidth = Math.floor(naturalHeight * aspectRatio)
      cropX = Math.floor((naturalWidth - cropWidth) / 2)
    } else {
      cropHeight = Math.floor(naturalWidth / aspectRatio)
      cropY = Math.floor((naturalHeight - cropHeight) / 2)
    }

    canvas.width = cropWidth
    canvas.height = cropHeight

    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight,
    )

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob)
        },
        "image/jpeg",
        0.9,
      )
    })
  }

  const handleConfirmCrop = async () => {
    if (!selectedFile) return

    const croppedBlob = await getCroppedImage()
    if (!croppedBlob) {
      setError("Failed to process image")
      setIsDialogOpen(false)
      return
    }

    setIsUploading(true)
    try {
      const croppedFile = new File([croppedBlob], selectedFile.name, {
        type: "image/jpeg",
      })

      const result = await uploadFile(croppedFile)

      if (result.success && result.url) {
        onUpload(result.url)
        setIsDialogOpen(false)
        setSelectedFile(null)
        setImageUrl(null)
      } else {
        setError(result.error || "Upload failed")
      }
    } catch {
      setError("Upload failed")
    } finally {
      setIsUploading(false)
    }
  }

  const handleCancelCrop = () => {
    setIsDialogOpen(false)
    setSelectedFile(null)
    setImageUrl(null)
  }

  const handleReplace = () => {
    inputRef.current?.click()
  }

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-foreground">{label}</p>
          <span className="text-xs text-dashboard-muted">(16:9 aspect ratio)</span>
        </div>
      )}

      {currentUrl ? (
        <div className="space-y-3">
          <div className="relative overflow-hidden rounded-xl border bg-muted">
            <img
              src={resolveMediaUrl(currentUrl)}
              alt="Thumbnail"
              className="h-44 w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 hover:opacity-100 transition-opacity">
              <div className="absolute bottom-3 left-3 right-3 flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={handleReplace}
                  className="flex-1"
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Change
                </Button>
                {onRemove && (
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    onClick={onRemove}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            "flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-muted-foreground/25 bg-muted/30 hover:bg-muted/50 hover:border-primary/40 cursor-pointer transition-all h-44",
            isUploading && "pointer-events-none opacity-60",
          )}
          onClick={() => inputRef.current?.click()}
        >
          {isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-dashboard-muted" />
              <p className="text-sm text-dashboard-muted">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 p-4 text-center">
              <div className="rounded-full bg-primary/10 p-3">
                <Upload className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Click to upload thumbnail</p>
                <p className="text-xs text-dashboard-muted mt-1">
                  You can adjust the crop area after selecting
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileSelect}
      />

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Move className="h-5 w-5" />
              Adjust Thumbnail
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-auto py-4">
            <p className="text-sm text-dashboard-muted mb-4">
              Your thumbnail will be center-cropped to a {aspectRatio.toFixed(2)}:1 aspect ratio.
            </p>

            {imageUrl && (
              <div className="flex justify-center bg-muted/50 rounded-lg p-2">
                <img
                  ref={imgRef}
                  src={imageUrl}
                  alt="Crop preview"
                  className="max-h-[50vh] w-auto object-contain"
                />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancelCrop}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmCrop}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Apply & Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
